import { useEffect, useState, useRef } from 'react';
import { getPyodideStatus, initPyodideWithRetry, PyodideStatus } from '../services/pythonExecution';

/**
 * Pyodide 预加载 Hook
 *
 * 在组件挂载时后台静默加载 Pyodide，提供加载状态和进度信息
 * 用于改善用户首次进入 Python 答题页面的体验
 */
export function usePyodidePreload() {
  const [status, setStatus] = useState<PyodideStatus>('idle');
  const [error, setError] = useState<string | undefined>(undefined);
  const [isLoaded, setIsLoaded] = useState(false);
  const [progress, setProgress] = useState(0);

  const progressTimerRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number>(0);

  // 模拟进度条更新
  const startProgressSimulation = () => {
    setProgress(0);
    let currentProgress = 0;

    // 清除之前的定时器
    if (progressTimerRef.current) {
      clearInterval(progressTimerRef.current);
    }

    progressTimerRef.current = setInterval(() => {
      // 非线性增长：前期快，后期慢，模拟真实加载过程
      if (currentProgress < 30) {
        currentProgress += 2;
      } else if (currentProgress < 60) {
        currentProgress += 1;
      } else if (currentProgress < 85) {
        currentProgress += 0.5;
      } else {
        // 停在 90%，等待实际加载完成
        currentProgress = 90;
        if (progressTimerRef.current) {
          clearInterval(progressTimerRef.current);
        }
      }
      setProgress(Math.min(currentProgress, 90));
    }, 200);
  };

  useEffect(() => {
    // 检查当前状态
    const currentStatus = getPyodideStatus();
    setStatus(currentStatus.state);

    if (currentStatus.state === 'ready') {
      setIsLoaded(true);
      setProgress(100);
      return;
    }

    if (currentStatus.state === 'error') {
      setError(currentStatus.error);
      return;
    }

    // 如果尚未加载，开始后台预加载
    if (currentStatus.state === 'idle') {
      let cancelled = false;

      const preload = async () => {
        startTimeRef.current = Date.now();
        setStatus('loading');
        startProgressSimulation();

        try {
          // 使用带重试的初始化函数
          await initPyodideWithRetry(2);

          if (!cancelled) {
            // 加载完成，进度跳到 100%
            setProgress(100);
            setStatus('ready');
            setIsLoaded(true);

            const loadTime = Date.now() - startTimeRef.current;

            // 埋点：记录加载成功
            if ((window as any).__PYODIDE_METRICS__) {
              (window as any).__PYODIDE_METRICS__.loadSuccessCount++;
              (window as any).__PYODIDE_METRICS__.totalLoadTime += loadTime;
            }
          }
        } catch (err: any) {
          if (!cancelled) {
            setProgress(0);
            setStatus('error');
            setError(err.message || 'Pyodide 加载失败');
            console.error('[Pyodide] 预加载失败:', err);

            // 埋点：记录加载失败
            if ((window as any).__PYODIDE_METRICS__) {
              (window as any).__PYODIDE_METRICS__.loadFailCount++;
            }
          }
        } finally {
          // 清理进度定时器
          if (progressTimerRef.current) {
            clearInterval(progressTimerRef.current);
            progressTimerRef.current = null;
          }
        }
      };

      // 延迟 500ms 开始加载，避免阻塞首屏渲染
      const timer = setTimeout(preload, 500);

      return () => {
        cancelled = true;
        clearTimeout(timer);
        if (progressTimerRef.current) {
          clearInterval(progressTimerRef.current);
        }
      };
    }
  }, []);

  return {
    status,
    error,
    isLoaded,
    isLoading: status === 'loading',
    isError: status === 'error',
    isReady: status === 'ready',
    progress, // 0-100 的进度值
  };
}
